<template>
  <!-- Le rappel du quiz du jour (REDMINE-13730) : l'heure de la notification
       que l'app planifie elle-meme, et ses messages, qui tournent un par jour
       dans l'ordre de la liste. Une seule ligne de reglages, posee par la
       migration ; les messages se creent, se modifient et se suppriment. -->
  <v-card outlined class="my-4 quiz-reminder">
    <v-card-title class="d-flex align-center">
      {{ $t('intranet.homeContent.quizReminder.title') }}
      <v-spacer></v-spacer>
      <v-btn small depressed color="primary" :disabled="!settings" @click="editTime()">
        {{ $t('intranet.homeContent.quizReminder.editTime') }}
      </v-btn>
    </v-card-title>
    <v-card-subtitle>{{ $t('intranet.homeContent.quizReminder.intro') }}</v-card-subtitle>
    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
      <p v-else-if="!settings">{{ $t('intranet.homeContent.quizReminder.missing') }}</p>
      <template v-else>
        <div class="d-flex align-center mb-2">
          <v-chip class="mr-4" label>{{ time(settings.hour, settings.minute) }}</v-chip>
          <span class="text-caption">{{ $t('intranet.homeContent.quizReminder.rotation') }}</span>
          <v-spacer></v-spacer>
          <v-btn small outlined color="primary" @click="editMessage()">
            {{ $t('intranet.homeContent.quizReminder.addMessage') }}
          </v-btn>
        </div>
        <p v-if="!messages.length" class="error--text">{{ $t('intranet.homeContent.quizReminder.noMessage') }}</p>
        <v-simple-table v-else dense>
          <tbody>
            <tr v-for="(message, index) in messages" :key="message.id" :class="{ 'text--disabled': !message.published }">
              <td class="text-no-wrap">{{ index + 1 }}</td>
              <td>
                <strong>{{ translated(message.title) || $t('intranet.homeContent.quizReminder.noTitle') }}</strong>
                — {{ translated(message.body) }}
              </td>
              <td class="text-no-wrap">
                <v-chip x-small label :color="message.published ? 'success' : ''">
                  {{ message.published ? $t('intranet.homeContent.published') : $t('intranet.homeContent.draft') }}
                </v-chip>
              </td>
              <td class="text-no-wrap text-right">
                <v-btn icon small :disabled="moving || index === 0" :title="$t('intranet.homeContent.featuredRegion.moveUp')" @click="move(index, -1)">
                  <v-icon small>mdi-arrow-up</v-icon>
                </v-btn>
                <v-btn icon small :disabled="moving || index === messages.length - 1" :title="$t('intranet.homeContent.featuredRegion.moveDown')" @click="move(index, 1)">
                  <v-icon small>mdi-arrow-down</v-icon>
                </v-btn>
                <v-btn icon small :title="$t('intranet.homeContent.quizReminder.edit')" @click="editMessage(message)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small :title="$t('intranet.homeContent.quizReminder.delete')" @click="remove(message)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </template>
    </v-card-text>

    <v-dialog v-model="timeDialog" max-width="480" :persistent="saving">
      <v-card v-if="timeDraft">
        <v-card-title>{{ $t('intranet.homeContent.quizReminder.time') }}</v-card-title>
        <v-card-text class="d-flex">
          <v-select v-model="timeDraft.hour" :items="hours" :label="$t('intranet.homeContent.quizReminder.hour')" class="mr-4" />
          <v-select v-model="timeDraft.minute" :items="minutes" :label="$t('intranet.homeContent.quizReminder.minute')" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="saving" @click="timeDialog = false">{{ $t('intranet.referential.button.cancel') }}</v-btn>
          <v-btn depressed color="primary" :loading="saving" @click="saveTime()">{{ $t('intranet.referential.button.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="messageDialog" max-width="640" :persistent="saving">
      <v-card v-if="messageDraft">
        <v-card-title>
          {{ messageDraft.id ? $t('intranet.homeContent.quizReminder.editMessage') : $t('intranet.homeContent.quizReminder.addMessage') }}
        </v-card-title>
        <v-card-text>
          <AveineTranslatedField :value="messageDraft.title" :label="$t('intranet.homeContent.quizReminder.notificationTitle')" :required="[]" />
          <AveineTranslatedField :value="messageDraft.body" :label="$t('intranet.homeContent.quizReminder.body')" />
          <v-switch v-model="messageDraft.published" :label="$t('intranet.homeContent.quizReminder.inRotation')" />
          <div class="text-caption mt-2">{{ $t('intranet.homeContent.quizReminder.preview') }}</div>
          <v-sheet outlined rounded class="pa-3">
            <div class="text-caption">{{ time(settings.hour, settings.minute) }}</div>
            <div v-if="translated(messageDraft.title)"><strong>{{ translated(messageDraft.title) }}</strong></div>
            <div>{{ translated(messageDraft.body) }}</div>
          </v-sheet>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="saving" @click="messageDialog = false">{{ $t('intranet.referential.button.cancel') }}</v-btn>
          <v-btn depressed color="primary" :loading="saving" :disabled="!complete(messageDraft.body)" @click="saveMessage()">
            {{ $t('intranet.referential.button.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import AveineTranslatedField from '@/components/AveineTranslatedField.vue'
import { errorHandler } from 'aveine-toolbox'

const API = process.env.VUE_APP_API_HOST

/** Une traduction sans aucun texte vaut « pas de titre ». */
function emptyTranslation (translation) {
  return !translation || Object.values(translation).every(value => !value)
}

export default {
  name: 'AveineQuizReminderSettings',
  components: { AveineTranslatedField },
  data () {
    return {
      settings: null,
      messages: [],
      loading: false,
      moving: false,
      saving: false,
      timeDialog: false,
      timeDraft: null,
      messageDialog: false,
      messageDraft: null,
      hours: Array.from({ length: 24 }, (_, hour) => ({ value: hour, text: `${hour} h` })),
      minutes: [0, 15, 30, 45].map(minute => ({ value: minute, text: String(minute).padStart(2, '0') }))
    }
  },
  created () {
    this.fetch()
  },
  methods: {
    time (hour, minute) {
      return `${hour} h ${String(minute).padStart(2, '0')}`
    },
    translated (translation) {
      if (!translation) return ''
      return translation[this.$i18n.locale] || translation.fr || translation.en || ''
    },
    /** CRUD-Wine exige le français et l'anglais. */
    complete (translation) {
      return !!(translation && translation.fr && translation.en)
    },
    /** Le corps JSON:API brut : pas de vuex-orm pour une poignee de lignes. */
    async request (method, path, body) {
      const token = await this.$auth.getTokenSilently()
      const response = await fetch(`${API}${path}`, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/vnd.api+json' },
        body: body ? JSON.stringify(body) : undefined
      })
      const text = await response.text()
      const document = text ? JSON.parse(text) : {}
      if (!response.ok) {
        const error = (document.errors && document.errors[0]) || {}
        throw new Error(error.detail || error.title || `HTTP ${response.status}`)
      }
      return document
    },
    async fetch () {
      this.loading = true
      try {
        const [settings, messages] = await Promise.all([
          this.request('GET', '/quiz-reminder-settings'),
          this.request('GET', '/quiz-reminder-messages?sort=position,id&page[size]=100')
        ])
        this.settings = settings.data ? { id: settings.data.id, ...settings.data.attributes } : null
        this.messages = (messages.data || []).map(entry => ({ id: entry.id, ...entry.attributes }))
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    },
    editTime () {
      this.timeDraft = { hour: this.settings.hour, minute: this.settings.minute }
      this.timeDialog = true
    },
    async saveTime () {
      this.saving = true
      try {
        await this.request('PATCH', `/quiz-reminder-settings/${this.settings.id}`, {
          data: { type: 'quiz-reminder-settings', id: this.settings.id, attributes: this.timeDraft }
        })
        this.timeDialog = false
        this.$toastr.s(this.$t('intranet.homeContent.quizReminder.saved'))
        await this.fetch()
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.saving = false
      }
    },
    editMessage (message) {
      this.messageDraft = message
        ? { id: message.id, title: { ...(message.title || {}) }, body: { ...(message.body || {}) }, published: message.published }
        : { id: null, title: {}, body: {}, published: true }
      this.messageDialog = true
    },
    async saveMessage () {
      const draft = this.messageDraft
      const attributes = {
        title: emptyTranslation(draft.title) ? null : draft.title,
        body: draft.body,
        published: draft.published
      }
      this.saving = true
      try {
        if (draft.id) {
          await this.request('PATCH', `/quiz-reminder-messages/${draft.id}`, {
            data: { type: 'quiz-reminder-messages', id: draft.id, attributes }
          })
        } else {
          const last = this.messages.reduce((max, message) => Math.max(max, message.position), 0)
          await this.request('POST', '/quiz-reminder-messages', {
            data: { type: 'quiz-reminder-messages', attributes: { ...attributes, position: last + 1 } }
          })
        }
        this.messageDialog = false
        this.$toastr.s(this.$t('intranet.homeContent.quizReminder.saved'))
        await this.fetch()
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.saving = false
      }
    },
    async remove (message) {
      if (!window.confirm(this.$t('intranet.homeContent.quizReminder.deleteConfirmation'))) return
      try {
        await this.request('DELETE', `/quiz-reminder-messages/${message.id}`)
        await this.fetch()
      } catch (errors) {
        errorHandler(errors, this)
      }
    },
    /**
     * Echange deux messages voisins. Les positions sont renumerotees de 1 a n
     * au passage, comme pour les regions du profil.
     */
    async move (index, step) {
      const order = this.messages.slice()
      const [item] = order.splice(index, 1)
      order.splice(index + step, 0, item)

      this.moving = true
      try {
        for (const [position, entry] of order.entries()) {
          if (entry.position === position + 1) continue
          await this.request('PATCH', `/quiz-reminder-messages/${entry.id}`, {
            data: { type: 'quiz-reminder-messages', id: entry.id, attributes: { position: position + 1 } }
          })
        }
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.moving = false
        await this.fetch()
      }
    }
  }
}
</script>
